import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import history from '../../../history';
import {
    fetchTracking,
    archiveApplication,
    unarchiveApplication,
    updateTrackingSettings,
} from '../../../actions';

import TrackingRow, {
    rowMacroStateForRow,
    DEFAULT_FOLLOW_UP_MIN_DAYS,
    DEFAULT_FOLLOW_UP_MAX_DAYS,
} from '../../tracking/TrackingRow';
import ArchiveModal from '../../tracking/ArchiveModal';
import UnmatchedFrolloPanel from '../../tracking/UnmatchedFrolloPanel';

import AdminShell from '../layout/AdminShell';
import Button from '../ui/Button';
import SearchInput from '../ui/SearchInput';
import LazyListFooter from '../ui/LazyListFooter';
import useDebouncedValue from '../hooks/useDebouncedValue';
import useLazyList from '../hooks/useLazyList';
import { RefreshIcon } from '../ui/icons';

// Functional re-shell of tracking/Tracking.js in the admin2 layout. The
// filter/sort/threshold logic and the TrackingRow/ArchiveModal/
// UnmatchedFrolloPanel components are reused unchanged; only the wrapper
// chrome and navigation target (/admin/application/:id) differ.
const MACRO_PRIORITY = { stalled: 0, action_required: 1, in_progress: 2, complete: 3 };
const macroLabel = (key, range) => {
    if (key === 'stalled') return `Stalled · ${range.maxDays}d+`;
    if (key === 'action_required') return `Follow up · ${range.minDays}-${range.maxDays}d`;
    if (key === 'in_progress') return `Active · <${range.minDays}d`;
    if (key === 'complete') return 'Done';
    return 'All';
};
const macroHelp = (key, range) => {
    if (key === 'stalled') return `At least one task has been outstanding for ${range.maxDays}+ days. Consider escalating or archiving.`;
    if (key === 'action_required') return `At least one task has been outstanding for ${range.minDays}-${range.maxDays} days. Time to chase the client.`;
    if (key === 'in_progress') return `Some tasks are still outstanding but nothing has been waiting for ${range.minDays}+ days yet.`;
    if (key === 'complete') return 'Every task is done for every borrower on this application.';
    return 'All active applications.';
};
const ROWS_PER_CHUNK = 25;

// Everything the tracking search looks at, flattened once per fetch instead of
// per keystroke (see the same pattern in ApplicationsPage).
const buildSearchString = (row) => {
    const applicants = Object.values(row.applicants || {});
    const parts = [row.name || ''];
    applicants.forEach((applicant) => {
        parts.push(applicant.name || '');
        parts.push(applicant.email || '');
    });
    return parts.join(' ').toLowerCase();
};

const MACRO_TONE = {
    stalled: { fg: '#991B1B', bg: '#FEF2F2', border: '#FECACA', dot: '#EF4444' },
    action_required: { fg: '#92400E', bg: '#FFFBEB', border: '#FCD34D', dot: '#F59E0B' },
    in_progress: { fg: '#1D4ED8', bg: '#EFF6FF', border: '#BFDBFE', dot: '#3B82F6' },
    complete: { fg: '#065F46', bg: '#ECFDF5', border: '#A7F3D0', dot: '#10B981' },
};

const TrackingPage = () => {
    const user = useSelector((s) => s.user);
    const organisation = useSelector((s) => s.organisation);
    const rows = useSelector((s) => s.tracking.rows);
    const loading = useSelector((s) => s.tracking.loading);
    const error = useSelector((s) => s.tracking.error);
    const dispatch = useDispatch();

    const [search, setSearch] = useState('');
    const [showArchived, setShowArchived] = useState(false);
    const [archiveTarget, setArchiveTarget] = useState(null);
    const [archiveOpen, setArchiveOpen] = useState(false);
    const [macroFilter, setMacroFilter] = useState('all');
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [minDraft, setMinDraft] = useState('');
    const [maxDraft, setMaxDraft] = useState('');
    const [settingsSaving, setSettingsSaving] = useState(false);

    // The box stays instant; only the filtering waits for the typing to settle.
    const debouncedSearch = useDebouncedValue(search, 200);

    // Port of componentDidMount/componentDidUpdate: fetch once a token exists.
    useEffect(() => {
        if (user.token) {
            dispatch(fetchTracking());
        }
    }, [user.token, dispatch]);

    const getRange = () => {
        const settings = (organisation && organisation.settings) || {};
        let min = settings.tracking_follow_up_min_days;
        let max = settings.tracking_follow_up_max_days;
        if (typeof min !== 'number' || min < 1) min = DEFAULT_FOLLOW_UP_MIN_DAYS;
        if (typeof max !== 'number' || max < 1) max = DEFAULT_FOLLOW_UP_MAX_DAYS;
        if (max <= min) max = min + 1;
        return { minDays: min, maxDays: max };
    };

    // Resolved once per render and shared by the helpers below, so the memos
    // further down can key off the two day numbers rather than a fresh object.
    const range = getRange();
    const { minDays, maxDays } = range;

    const openSettings = () => {
        setMinDraft(String(range.minDays));
        setMaxDraft(String(range.maxDays));
        setSettingsOpen(true);
    };

    const saveSettings = async () => {
        const min = parseInt(minDraft, 10);
        const max = parseInt(maxDraft, 10);
        if (!isFinite(min) || !isFinite(max) || min < 1 || max < 2 || max <= min || max > 365) {
            return;
        }
        setSettingsSaving(true);
        try {
            await dispatch(
                updateTrackingSettings({
                    tracking_follow_up_min_days: min,
                    tracking_follow_up_max_days: max,
                })
            );
            setSettingsOpen(false);
        } finally {
            setSettingsSaving(false);
        }
    };

    const openArchive = (row) => {
        setArchiveTarget(row);
        setArchiveOpen(true);
    };

    const closeArchive = () => {
        setArchiveOpen(false);
        setArchiveTarget(null);
    };

    const confirmArchive = async (reason) => {
        if (!archiveTarget) return;
        await dispatch(archiveApplication(archiveTarget.application_id, reason));
        closeArchive();
    };

    const handleUnarchive = async (row) => {
        await dispatch(unarchiveApplication(row.application_id));
    };

    const openApplication = (row) => {
        history.push(`/admin/application/${row.application_id}`);
    };

    const renderMacroChip = (key, count) => {
        const active = macroFilter === key;
        const tone = MACRO_TONE[key];
        return (
            <button
                key={key}
                type="button"
                onClick={() => setMacroFilter(key)}
                title={macroHelp(key, range)}
                className="inline-flex items-center gap-2 text-xs font-semibold rounded-xl px-3 py-2 shrink-0 transition-colors"
                style={{
                    backgroundColor: active ? (tone ? tone.bg : '#1A1D2A') : '#FFFFFF',
                    color: active ? (tone ? tone.fg : '#FFFFFF') : '#6B7280',
                    border: `1px solid ${active ? (tone ? tone.border : '#1A1D2A') : '#E5E7EB'}`,
                    cursor: 'pointer',
                }}
            >
                {tone ? (
                    <span className="inline-block rounded-full" style={{ width: 7, height: 7, backgroundColor: tone.dot }} />
                ) : null}
                {macroLabel(key, range)}
                <span style={{ opacity: 0.7 }}>· {count}</span>
            </button>
        );
    };

    const renderSettingsPopover = () => {
        if (!settingsOpen) return null;
        const min = parseInt(minDraft, 10);
        const max = parseInt(maxDraft, 10);
        const validMin = isFinite(min) && min >= 1 && min <= 364;
        const validMax = isFinite(max) && max >= 2 && max <= 365 && (!isFinite(min) || max > min);
        const valid = validMin && validMax;
        return (
            <div
                className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
                style={{ backgroundColor: 'rgba(15, 17, 26, 0.55)', backdropFilter: 'blur(2px)' }}
                onClick={() => setSettingsOpen(false)}
            >
                <div
                    className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl p-5 shadow-xl border border-[#E5E7EB]"
                    onClick={(e) => e.stopPropagation()}
                >
                    <h3 className="text-base font-bold mb-1 text-[#1F2937]">Follow-up thresholds</h3>
                    <p className="text-xs mb-4 text-[#6B7280]">
                        Configure when an application escalates from Active → Follow up → Stalled.
                    </p>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                        <label className="flex flex-col gap-1">
                            <span className="text-[11px] uppercase tracking-wider font-bold text-[#6B7280]">Follow up after</span>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    min={1}
                                    max={364}
                                    value={minDraft}
                                    onChange={(e) => setMinDraft(e.target.value)}
                                    className="rounded-xl text-sm w-20 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#7C5CFC]"
                                    style={{
                                        border: `1px solid ${validMin ? '#E5E7EB' : '#FCA5A5'}`,
                                        backgroundColor: '#FFFFFF',
                                        color: '#1F2937',
                                    }}
                                />
                                <span className="text-sm text-[#6B7280]">days</span>
                            </div>
                        </label>
                        <label className="flex flex-col gap-1">
                            <span className="text-[11px] uppercase tracking-wider font-bold text-[#6B7280]">Stalled after</span>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    min={2}
                                    max={365}
                                    value={maxDraft}
                                    onChange={(e) => setMaxDraft(e.target.value)}
                                    className="rounded-xl text-sm w-20 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#7C5CFC]"
                                    style={{
                                        border: `1px solid ${validMax ? '#E5E7EB' : '#FCA5A5'}`,
                                        backgroundColor: '#FFFFFF',
                                        color: '#1F2937',
                                    }}
                                />
                                <span className="text-sm text-[#6B7280]">days</span>
                            </div>
                        </label>
                    </div>
                    <div className="rounded-xl p-3 mb-4 text-xs" style={{ backgroundColor: '#F5F6FA', color: '#6B7280', border: '1px solid #E5E7EB' }}>
                        <div className="flex items-center gap-2 mb-1">
                            <span className="inline-block rounded-full" style={{ width: 7, height: 7, backgroundColor: '#3B82F6' }} />
                            <span><strong>Active</strong>: outstanding less than {validMin ? min : '?'} days</span>
                        </div>
                        <div className="flex items-center gap-2 mb-1">
                            <span className="inline-block rounded-full" style={{ width: 7, height: 7, backgroundColor: '#F59E0B' }} />
                            <span><strong>Follow up</strong>: outstanding {validMin ? min : '?'}-{validMax ? max : '?'} days</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="inline-block rounded-full" style={{ width: 7, height: 7, backgroundColor: '#EF4444' }} />
                            <span><strong>Stalled</strong>: outstanding {validMax ? max : '?'}+ days</span>
                        </div>
                    </div>
                    <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
                        <Button variant="secondary" onClick={() => setSettingsOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={saveSettings} disabled={!valid || settingsSaving}>
                            {settingsSaving ? 'Saving…' : 'Save'}
                        </Button>
                    </div>
                </div>
            </div>
        );
    };

    // activeRows already carry their _macro state (computed once below), so the
    // chip counts are a plain tally rather than a second pass of date maths.
    const renderToolbar = (activeRows, archivedCount) => {
        const counts = activeRows.reduce(
            (acc, r) => {
                acc[r._macro.state] = (acc[r._macro.state] || 0) + 1;
                acc.all++;
                return acc;
            },
            { all: 0, stalled: 0, action_required: 0, in_progress: 0, complete: 0 }
        );
        return (
            <div className="flex flex-col gap-3 mb-5">
                <div className="flex flex-wrap items-center gap-2">
                    <SearchInput
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search applicant or application…"
                        className="flex-1 min-w-[220px]"
                    />
                    <button
                        type="button"
                        onClick={() => setShowArchived((s) => !s)}
                        className="text-xs font-semibold rounded-xl px-3 py-2.5 shrink-0 transition-colors"
                        style={{
                            backgroundColor: showArchived ? '#1A1D2A' : '#FFFFFF',
                            color: showArchived ? '#FFFFFF' : '#6B7280',
                            border: `1px solid ${showArchived ? '#1A1D2A' : '#E5E7EB'}`,
                            cursor: 'pointer',
                        }}
                    >
                        {showArchived ? 'Hide' : 'Show'} archived · {archivedCount}
                    </button>
                    <button
                        type="button"
                        onClick={openSettings}
                        title="Configure follow-up and stalled thresholds"
                        className="text-xs font-semibold rounded-xl px-3 py-2.5 shrink-0"
                        style={{ backgroundColor: '#FFFFFF', color: '#6B7280', border: '1px solid #E5E7EB', cursor: 'pointer' }}
                    >
                        ⚙ {range.minDays}-{range.maxDays}d
                    </button>
                    <button
                        type="button"
                        onClick={() => dispatch(fetchTracking())}
                        title="Refresh"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-xl px-3 py-2.5 shrink-0"
                        style={{ backgroundColor: '#FFFFFF', color: '#6B7280', border: '1px solid #E5E7EB', cursor: 'pointer' }}
                    >
                        <RefreshIcon className="h-3.5 w-3.5" />
                        Refresh
                    </button>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {renderMacroChip('all', counts.all)}
                    {renderMacroChip('stalled', counts.stalled)}
                    {renderMacroChip('action_required', counts.action_required)}
                    {renderMacroChip('in_progress', counts.in_progress)}
                    {renderMacroChip('complete', counts.complete)}
                </div>
            </div>
        );
    };

    // `lazy` is the useLazyList handle for this section: only its current chunk
    // is mounted, and its footer both reports progress and pulls in the next.
    const renderRows = (lazy, isArchivedSection) => {
        if (!lazy.total) {
            return (
                <div
                    className="text-sm rounded-2xl py-10 text-center"
                    style={{ backgroundColor: '#FFFFFF', color: '#6B7280', border: '1px dashed #D1D5DB' }}
                >
                    {isArchivedSection ? 'No archived applications.' : 'No active applications match your filters.'}
                </div>
            );
        }
        return (
            <>
                {lazy.visible.map((row) => (
                    <TrackingRow
                        key={row.application_id}
                        row={row}
                        range={range}
                        onArchive={() => openArchive(row)}
                        onUnarchive={() => handleUnarchive(row)}
                        onOpenApplication={() => openApplication(row)}
                    />
                ))}
                <LazyListFooter
                    sentinelRef={lazy.sentinelRef}
                    hasMore={lazy.hasMore}
                    onLoadMore={lazy.loadMore}
                    visibleCount={lazy.visibleCount}
                    total={lazy.total}
                    noun="applications"
                />
            </>
        );
    };

    // Same filtering/sorting as the original render(), split into memoised
    // passes so a keystroke only re-runs the cheap substring filter: the
    // search strings, the macro states and the sort are all cached.
    const searchIndex = useMemo(
        () => (rows || []).map((row) => ({ row, haystack: buildSearchString(row) })),
        [rows]
    );

    const filtered = useMemo(() => {
        const q = debouncedSearch.trim().toLowerCase();
        const matches = q === '' ? searchIndex : searchIndex.filter((entry) => entry.haystack.includes(q));
        return matches.map((entry) => entry.row);
    }, [searchIndex, debouncedSearch]);

    const archived = useMemo(() => filtered.filter((r) => r.tracking_status === 'archived'), [filtered]);

    // Active rows before the macro chip filter: the chip counts come from here,
    // so each row's macro state is computed once and reused by both.
    const activeWithMacro = useMemo(
        () =>
            filtered
                .filter((r) => r.tracking_status !== 'archived')
                .map((r) => Object.assign({}, r, { _macro: rowMacroStateForRow(r, { minDays, maxDays }) })),
        [filtered, minDays, maxDays]
    );

    const active = useMemo(() => {
        const list =
            macroFilter === 'all'
                ? [...activeWithMacro]
                : activeWithMacro.filter((r) => r._macro.state === macroFilter);
        list.sort((a, b) => {
            const pa = MACRO_PRIORITY[a._macro.state] ?? 99;
            const pb = MACRO_PRIORITY[b._macro.state] ?? 99;
            if (pa !== pb) return pa - pb;
            if (a._macro.state === 'complete') {
                return new Date(b.last_activity || b.created || 0) - new Date(a.last_activity || a.created || 0);
            }
            return (b._macro.worstDays || 0) - (a._macro.worstDays || 0);
        });
        return list;
    }, [activeWithMacro, macroFilter]);

    const activeList = useLazyList(active, { pageSize: ROWS_PER_CHUNK });
    const archivedList = useLazyList(archived, { pageSize: ROWS_PER_CHUNK });

    return (
        <AdminShell title="Tracking" maxWidth="max-w-6xl">
            {renderSettingsPopover()}
            <ArchiveModal
                open={archiveOpen}
                applicationName={
                    archiveTarget &&
                    (Object.values(archiveTarget.applicants || {})
                        .map((a) => a.name)
                        .filter(Boolean)
                        .join(' & ') ||
                        archiveTarget.name)
                }
                onClose={closeArchive}
                onConfirm={confirmArchive}
            />

            <div className="mb-5">
                <p className="text-sm text-[#6B7280]">
                    Lead and document tracker across every application. Open this each day to see who needs
                    following up and what's missing.
                </p>
            </div>

            <UnmatchedFrolloPanel />

            {renderToolbar(activeWithMacro, archived.length)}

            {loading ? (
                <div
                    className="text-sm rounded-xl px-4 py-3 mb-3"
                    style={{ backgroundColor: '#FFFFFF', color: '#6B7280', border: '1px solid #E5E7EB' }}
                >
                    Loading tracking data…
                </div>
            ) : null}

            {error ? (
                <div
                    className="text-sm rounded-xl px-4 py-3 mb-3"
                    style={{ backgroundColor: '#FEF2F2', color: '#991B1B', border: '1px solid #FECACA' }}
                >
                    Failed to load tracking data: {error}
                </div>
            ) : null}

            {renderRows(activeList, false)}

            {showArchived ? (
                <div className="mt-8">
                    <div className="flex items-center gap-2 mb-3">
                        <h3 className="text-sm font-bold uppercase tracking-wider text-[#6B7280]">
                            Previous applications
                        </h3>
                        <span style={{ color: '#D1D5DB' }}>·</span>
                        <span className="text-xs text-[#9CA3AF]">{archived.length}</span>
                    </div>
                    {renderRows(archivedList, true)}
                </div>
            ) : null}
        </AdminShell>
    );
};

export default TrackingPage;
